/**
 * Parse de data no formato brasileiro dd/MM/yyyy
 * @param {string} ddmmyyyy - Data no formato dd/MM/yyyy
 * @returns {Date|null} Objeto Date ou null se inválido
 */
export function parseDateBR(ddmmyyyy) {
    if (!ddmmyyyy || typeof ddmmyyyy !== 'string' || ddmmyyyy.length < 8) return null;
    const [dd, mm, yy] = ddmmyyyy.split('/').map((n) => parseInt(n, 10));
    if (!dd || !mm || !yy) return null;
    const dt = new Date(yy, mm - 1, dd);
    // Verifica coerência (Date corrige automaticamente datas inválidas)
    if (dt.getFullYear() !== yy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
    return dt;
}

/**
 * Parse de horário no formato HH:mm
 * @param {string} hhmm - Horário no formato HH:mm
 * @returns {{hh: number, mm: number}|null} Objeto com horas e minutos ou null se inválido
 */
export function parseTimeHHmm(hhmm) {
    if (!hhmm || typeof hhmm !== 'string' || hhmm.length < 4) return null;
    const [hh, mm] = hhmm.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return { hh, mm };
}

/**
 * Combina data e hora em um único objeto Date
 * @param {Date} dateOnly - Objeto Date com apenas a data
 * @param {{hh: number, mm: number}} timeOnly - Objeto com horas e minutos
 * @returns {Date|null} Objeto Date combinado ou null se inválido
 */
export function combineDateTime(dateOnly, timeOnly) {
    if (!dateOnly || !timeOnly) return null;
    const dt = new Date(
        dateOnly.getFullYear(),
        dateOnly.getMonth(),
        dateOnly.getDate(),
        timeOnly.hh,
        timeOnly.mm,
        0,
        0
    );
    return dt;
}

/**
 * Parse de string de data em múltiplos formatos
 * Suporta: dd/MM/yyyy HH:mm:ss, dd/MM/yyyy HH:mm, ISO 8601
 * @param {string} dateStr - String de data
 * @returns {Date|null} Objeto Date ou null se inválido
 */
export function parseDate(dateStr) {
    if (!dateStr) return null;

    // Formato brasileiro: dd/MM/yyyy HH:mm:ss ou dd/MM/yyyy HH:mm
    if (dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        const dateParts = parts[0].split('/');
        if (dateParts.length === 3) {
            const timeParts = parts[1] ? parts[1].split(':') : [0, 0, 0];
            const year = parseInt(dateParts[2], 10);
            const month = parseInt(dateParts[1], 10) - 1;
            const day = parseInt(dateParts[0], 10);
            const hour = parseInt(timeParts[0] || 0, 10);
            const minute = parseInt(timeParts[1] || 0, 10);
            const second = parseInt(timeParts[2] || 0, 10);

            return new Date(year, month, day, hour, minute, second);
        }
    }

    // Formato ISO ou outros
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;

    return null;
}

/**
 * Formata data para exibição no formato dd/MM/yyyy
 * @param {string|Date} dateStr - String de data ou objeto Date
 * @returns {string} Data formatada ou string original se inválida
 */
export function formatDate(dateStr) {
    const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr;
    if (!date) return dateStr || '';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
}

/**
 * Formata data e hora para exibição no formato dd/MM/yyyy HH:mm
 * @param {string|Date} dateStr - String de data ou objeto Date
 * @returns {string} Data e hora formatadas ou string original se inválida
 */
export function formatDateTime(dateStr) {
    const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr;
    if (!date) return dateStr || '';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

/**
 * Formata data e hora para envio à API no formato yyyy-MM-dd HH:mm:ss
 * @param {string} dateStr - Data no formato dd/MM/yyyy
 * @param {string} timeStr - Hora no formato HH:mm
 * @returns {string|null} Data formatada para API ou null se inválido
 */
export function formatDateTimeForApi(dateStr, timeStr) {
    const parsedDate = parseDateBR(dateStr);
    const parsedTime = parseTimeHHmm(timeStr);
    if (!parsedDate || !parsedTime) return null;

    const dateTime = combineDateTime(parsedDate, parsedTime);
    const pad = (value) => String(value).padStart(2, '0');

    return `${dateTime.getFullYear()}-${pad(dateTime.getMonth() + 1)}-${pad(dateTime.getDate())} ${pad(dateTime.getHours())}:${pad(dateTime.getMinutes())}:00`;
}

/**
 * Garante que um horário tenha segundos (HH:mm:ss)
 * @param {string} value - Horário em qualquer formato
 * @returns {string} Horário no formato HH:mm:ss
 */
export function ensureTimeWithSeconds(value) {
    if (!value) return '00:00:00';
    const trimmed = value.toString().trim();
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
    if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
    return trimmed;
}

/**
 * Extrai data e hora de uma string no formato dd/MM/yyyy HH:mm[:ss]
 * @param {string} value - String com data e hora
 * @returns {{date: string, time: string}} Objeto com data e hora separadas
 */
export function extractDateAndTime(value) {
    if (!value || typeof value !== 'string') return { date: '', time: '' };

    const parts = value.trim().split(/[T ]/); // aceita espaço ou 'T'
    let datePart = parts[0] || '';
    let timePart = (parts[1] || '').trim();

    if (timePart) {
        // remove segundos se existirem
        const [hh = '', mm = ''] = timePart.split(':');
        const hhPadded = String(hh).padStart(2, '0');
        const mmPadded = mm !== '' ? String(mm).padStart(2, '0') : '00';
        timePart = hhPadded + ':' + mmPadded;
    }

    return { date: datePart, time: timePart };
}

/**
 * Normaliza horário removendo segundos se existirem
 * @param {string} timeStr - Horário em qualquer formato
 * @returns {string} Horário no formato HH:mm
 */
export function normalizeTime(timeStr) {
    if (!timeStr) return '';
    const trimmed = String(timeStr).trim();
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
        return trimmed.substring(0, 5); // Remove os segundos
    }
    return trimmed;
}
